import Keycloak, { type KeycloakProfile } from 'keycloak-js'
import { useUserStore } from '@/stores/user'
import { getKeycloakClientId, getKeycloakRealm, getKeycloakUrl } from '@/global/environmentGetters'
import { requireString } from '@/util/requireString'
import {
  PUSH_NOTIFICATIONS_ACTIVE_KEY,
  PUSH_NOTIFICATIONS_POPUP_KEY
} from '@/composables/useLocalStoragePerUser'

export class AuthenticationService {
  private static readonly MIN_REMAINING_TOKEN_VALIDITY_SECONDS = 60

  private static keycloakInstance: Keycloak
  private static refreshInFlight: Promise<string> | null = null

  static initializeKeycloak(): void {
    AuthenticationService.keycloakInstance = new Keycloak({
      realm: getKeycloakRealm(),
      url: getKeycloakUrl(),
      clientId: getKeycloakClientId()
    })

    const kc = AuthenticationService.keycloakInstance

    kc.onTokenExpired = () => {
      console.info('[Keycloak] Access token expired, attempting refresh via onTokenExpired')
      AuthenticationService.updateAccessToken().catch((e) => {
        console.error('[Keycloak] Token refresh failed in onTokenExpired handler:', e)
      })
    }

    // Guards against browser timer throttling in backgrounded PWAs.
    setInterval(
      () => {
        if (kc.authenticated) {
          AuthenticationService.updateAccessToken().catch(() => {
            // Error logged inside updateAccessToken
          })
        }
      },
      4 * 60 * 1000
    )

    // Crucial for when the user clicks a push notification after being backgrounded.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && kc.authenticated) {
        console.info('[Keycloak] App returned to foreground, attempting token refresh')
        AuthenticationService.updateAccessToken().catch(() => {
          // Error logged inside updateAccessToken
        })
      }
    })
  }

  static async loginUser(): Promise<void> {
    await this.keycloakInstance.login()
  }

  static async isAlreadyLoggedIn(): Promise<boolean> {
    const isLocal = window.location.hostname === 'localhost'
    try {
      if (isLocal) {
        // On localhost: use original behavior — no silent SSO iframe
        // (Keycloak CSP blocks framing of auth.tcc-clinicalsolutions.de from localhost)
        return await AuthenticationService.keycloakInstance.init({
          onLoad: 'check-sso',
          checkLoginIframe: false
        })
      }

      // On real environments (dev, staging, prod): use silent SSO iframe
      // which allows session restoration after page refresh without redirect to login
      return await AuthenticationService.keycloakInstance.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${location.origin}${import.meta.env.BASE_URL}silent-check-sso.html`,
        checkLoginIframe: false
      })
    } catch (error) {
      console.error('Keycloak init error: ' + (error as Error).message)
      return false
    }
  }

  static async initializeApp(): Promise<void> {
    try {
      const userProfile = await this.getKeycloakProfile()
      const medicalTitle =
        AuthenticationService.keycloakInstance.tokenParsed?.['telehealth']?.['medical_title']
      useUserStore().setUser({
        userName: requireString(userProfile.username),
        title: medicalTitle,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        email: requireString(userProfile.email),
        userRights: []
      })
    } catch (error) {
      console.error('[Keycloak] Loading user profile error:', (error as Error).message)
    }
  }

  static async getKeycloakProfile(): Promise<KeycloakProfile> {
    return await AuthenticationService.keycloakInstance.loadUserProfile()
  }

  static async updateAccessToken(): Promise<string> {
    if (AuthenticationService.refreshInFlight) {
      return AuthenticationService.refreshInFlight
    }

    AuthenticationService.refreshInFlight = (async () => {
      const kc = AuthenticationService.keycloakInstance

      if (!kc.token) {
        throw Error('[Keycloak] Cannot refresh — no token present')
      }

      // If updateToken fails (e.g., network error), Keycloak JS clears these fields.
      const rtExp = kc.refreshTokenParsed?.exp
      const isRefreshTokenExpiredBeforeCall = rtExp ? Date.now() / 1000 >= rtExp : false

      try {
        await kc.updateToken(AuthenticationService.MIN_REMAINING_TOKEN_VALIDITY_SECONDS)
        return kc.token!
      } catch (err) {
        if (isRefreshTokenExpiredBeforeCall) {
          await AuthenticationService.logout()
          throw err
        }

        // Did Keycloak actively reject the refresh token? (e.g. Session killed by Admin)
        const isOAuthError = err && typeof err === 'object' && 'error' in err
        if (isOAuthError) {
          await AuthenticationService.logout()
          throw err
        }

        // Otherwise, it's a transient failure (network timeout, offline).
        // DO NOT log out. The token might be expired locally, but the session is
        // still alive in Keycloak. We will retry on the next visibilitychange/interval.
        console.error(
          '[Keycloak] Token refresh failed due to network error — session preserved',
          err
        )
        throw err
      }
    })()

    try {
      return await AuthenticationService.refreshInFlight
    } finally {
      AuthenticationService.refreshInFlight = null
    }
  }

  static async logout(): Promise<void> {
    // Per-user push notification preferences must survive logout so the switch
    // can be restored on the next login (they are keyed by user name, not session).
    const preservedKeys = [PUSH_NOTIFICATIONS_POPUP_KEY, PUSH_NOTIFICATIONS_ACTIVE_KEY]
    const preservedEntries = preservedKeys
      .map((key) => [key, localStorage.getItem(key)] as const)
      .filter(([, value]) => value !== null)

    localStorage.clear()

    for (const [key, value] of preservedEntries) {
      localStorage.setItem(key, value as string)
    }

    try {
      await AuthenticationService.keycloakInstance.logout({
        redirectUri: `${window.location.origin}${import.meta.env.BASE_URL}`
      })
    } catch (e) {
      console.error('[Keycloak] Logout error:', e)
    }
  }
}
